import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, contentChild, input, output, signal, viewChild } from '@angular/core';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { ButtonComponent, ButtonIconOnlyComponent } from '@reconcept-platform/shared/ui/standalone/button';
import { RcpPopoverDirective } from '@reconcept-platform/shared/ui/standalone/popover';
import { DropdownMenuTriggerDirective } from './dropdown-menu-trigger.directive';

@Component({
  selector: 'rcp-dropdown-menu',
  styles: `
    :host {
      display: block;
      font-weight: normal;
    }

    .rcp-dropdown-content {
      padding: var(--item-padding-md);
      display: flex;
      flex-direction: column;
      gap: var(--item-internal-spacing-sm);
    }

    .dropdown-menu-custom-trigger {
      display: inline-flex;
      cursor: pointer;
    }
  `,
  template: `
    <div class="dropdown-menu-button">
      <!--  the {{ label() }} adds an element to the content of button, so even when empty it shows the element for the label  -->
      <!--  the else statement is used to show the button without the label, so the button can be used with only an icon  -->
      @if (customTrigger(); as trigger) {
        <div
          class="dropdown-menu-custom-trigger"
          [class.active]="showDropdownMenu()"
          [rcpPopover]="context"
          [rcpPopoverIsEnabled]="!disabled()"
          rcpPopoverVariation="menuOverModal"
          rcpPopoverTrigger="manual"
          (click)="clickToggleButton($event)"
          (rcpPopoverIsVisibleChange)="setDropdownActive($event)"
        >
          <ng-container [ngTemplateOutlet]="trigger.templateRef" />
        </div>
      } @else if (label()) {
        <rcp-button
          [icon]="icon()"
          [color]="color() ?? 'neutral'"
          [active]="showDropdownMenu()"
          [disabled]="disabled()"
          [rcpPopover]="context"
          [rcpPopoverIsEnabled]="!disabled()"
          rcpPopoverVariation="menuOverModal"
          rcpPopoverTrigger="manual"
          (click)="clickToggleButton($event)"
          (rcpPopoverIsVisibleChange)="setDropdownActive($event)"
        >
          {{ label() }}
        </rcp-button>
      } @else if (icon(); as icon) {
        <rcp-button-icon-only
          [icon]="icon"
          [active]="showDropdownMenu()"
          [disabled]="disabled()"
          [rcpPopover]="context"
          [rcpPopoverIsEnabled]="!disabled()"
          rcpPopoverVariation="menuOverModal"
          rcpPopoverTrigger="manual"
          (click)="clickToggleButton($event)"
          (rcpPopoverIsVisibleChange)="setDropdownActive($event)"
        />
      }
    </div>

    <ng-template #context>
      <div class="rcp-dropdown-content">
        <ng-content />
      </div>
    </ng-template>
  `,
  imports: [ButtonComponent, RcpPopoverDirective, ButtonIconOnlyComponent, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownMenuComponent {
  readonly label = input<string>();
  readonly icon = input<IconProp>();
  readonly color = input<'primary' | 'positive' | 'negative'>();
  readonly disabled = input<boolean>(false);
  readonly menuPosition = input<'above' | 'below'>('below');
  readonly blockOpen = input<boolean>(false);

  readonly clickButton = output<void>();

  readonly customTrigger = contentChild(DropdownMenuTriggerDirective);

  readonly tooltip = viewChild(RcpPopoverDirective);
  readonly showDropdownMenu = signal<boolean>(false);

  hide() {
    // @ts-expect-error TS2532 object possibly undefined
    this.tooltip().hide();
  }

  show() {
    // @ts-expect-error TS2532 object possibly undefined
    this.tooltip().show();
  }

  setDropdownActive(isVisible: boolean) {
    this.showDropdownMenu.set(isVisible);
  }

  clickToggleButton($event: MouseEvent) {
    $event.stopImmediatePropagation();
    if (!this.disabled()) {
      if (!this.blockOpen()) {
        this.toggleDropdown();
      }
      this.clickButton.emit();
    }
  }

  toggleDropdown() {
    // @ts-expect-error TS2532 object possibly undefined
    if (this.tooltip().rcpPopoverIsVisible()) {
      this.hide();
    } else {
      this.show();
    }
  }
}
